import * as React from "react";
import * as _ from "underscore";
import { Button } from "carbon-components-react";
import { Save16 } from "@carbon/icons-react";
import { connect } from "react-redux";
import Select from "react-select";

import { PropertyRepairShape } from "@eave/shared/types/api";
import HurdleView from "underwriter_dashboard/shared/HurdleView";
import { HurdleProps } from "underwriter_dashboard/HurdleEditor";
import APIClient from "../../../APIClient";
import { mapDispatchToProps } from "../selectors";

import RepairSummary from "./RepairSummary";
import RepairTable from "./RepairTable";

const styles = require("underwriter_dashboard/hurdle_editors/styles.module.scss");

interface AppraisalSummaryProps extends HurdleProps {
  loadApp: Function;
  modalTitle: string;
  repairs: {
    map: Function;
    length: number;
    [index: number]: PropertyRepairShape;
  };
}

// TODO: jjw: very similar to InspectionSummary; make parent class for both

class AppraisalSummary extends React.Component<AppraisalSummaryProps, any> {
  constructor(props) {
    super(props);
    this.state = AppraisalSummary.initialState(props);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.app.id !== this.props.app.id) {
      this.setState(AppraisalSummary.initialState(nextProps));
    }
  }

  static initialState(props) {
    const { app, repairs } = props;

    const complete = _.map(repairs, (r: any) => ({
      id: r.id || null,
      seller_fix_confirmed_complete: r.seller_fix_confirmed_complete || null,
    }));

    return {
      changesMade: false,
      willSupplyInspection: app.property_repairs_will_supply_inspection,
      remedyMethod: app.property_repairs_remedy_method,
      confirmedComplete: complete,
    };
  }

  onChangeComplete(repairId) {
    const updated = _.map(this.state.confirmedComplete, (r: any) => {
      if (r.id === repairId) {
        return {
          ...r,
          seller_fix_confirmed_complete: r.seller_fix_confirmed_complete ? null : true,
        };
      }
      return r;
    });

    this.setState({
      ...this.state,
      changesMade: true,
      confirmedComplete: updated,
    });
  }

  onChangeAppField(fieldName, e) {
    this.setState({
      ...this.state,
      changesMade: true,
      [fieldName]: e ? e.value : null,
    });
  }

  syncConfirmedCompleteIfNeeded(): any {
    const { app } = this.props;
    const { remedyMethod, confirmedComplete } = this.state;

    if (remedyMethod !== "seller_fix") {
      return Promise.resolve();
    }

    return APIClient.put(
      `/loan_applications/${app.id}/hurdles/property_condition/sync_confirmed_complete`,
      confirmedComplete,
    );
  }

  setStage(params) {
    return APIClient.put(
      `/loan_applications/${this.props.app.id}/hurdles/property_condition/stage`,
      params,
    );
  }

  stepBack() {
    const { app, loadApp } = this.props;

    if (!confirm("Step back to appraisal repairs?")) {
      return;
    }

    this.setStage({
      requested_inspection: false,
      will_supply_inspection: null,
      remedy_method: null,
    }).then(() => {
      loadApp(app.id);
    });
  }

  saveAndSendAAN() {
    const { app, loadApp, onHide } = this.props;

    if (!confirm("Save and send AAN?")) {
      return;
    }

    this.setStage({
      will_supply_inspection: false,
    })
      .then(() =>
        APIClient.post(`/${app.id}/aans`, {
          reasons: ["Property has ineligible repair(s) and borrower would not supply inspection"],
        }),
      )
      .then(() => {
        loadApp(app.id);
        onHide();
      });
  }

  onSave() {
    const { app, loadApp, onHide } = this.props;
    const { willSupplyInspection, remedyMethod } = this.state;

    if (
      remedyMethod === "renegotiate_price" &&
      !confirm("Save and set flag on PC to require addendum?")
    ) {
      return;
    }

    this.setStage({
      will_supply_inspection: willSupplyInspection,
      remedy_method: remedyMethod,
    })
      .then(this.syncConfirmedCompleteIfNeeded())
      .then(() => {
        loadApp(app.id);

        if (willSupplyInspection === null || remedyMethod !== null) {
          onHide();
        }
      });
  }

  ineligibleRepairsInAppraisal() {
    const repairs = _.chain(this.props.repairs)
      .filter((r: any) => !r.eligible)
      .filter((r: any) => r.in_appraisal)
      .value();
    return repairs.length > 0;
  }

  getSalesPrice() {
    const price = parseFloat(this.props.app.purchase_contract.salesPriceTotalAmount);
    if (!_.isNumber(price) || _.isNaN(price)) {
      throw new Error("could not parse purchase contract sales price");
    }
    return price;
  }

  getCostToCure() {
    return _.chain(this.props.repairs)
      .filter((r: any) => r.in_appraisal)
      .reduce((memo: number, r: any) => memo + parseFloat(r.appraisal_cost_to_cure), 0)
      .value();
  }

  getCostToCureAsPercent() {
    return (this.getCostToCure() / this.getSalesPrice()) * 100;
  }

  getRemedyOptions() {
    if (this.getCostToCureAsPercent() > 5) {
      return [{ value: "seller_fix", label: "C: Seller fix" }];
    }
    return [
      { value: "ignore_fix", label: "A: Ignore fix" },
      { value: "renegotiate_price", label: "B: Renegotiate price" },
      { value: "seller_fix", label: "C: Seller fix" },
    ];
  }

  render() {
    const { app, hurdle, onHide, modalTitle, repairs } = this.props;
    const { changesMade, willSupplyInspection, remedyMethod, confirmedComplete } = this.state;

    const showRemedy = willSupplyInspection === false && !this.ineligibleRepairsInAppraisal();

    let buttons;
    if (changesMade) {
      buttons = (
        <div style={{ flex: 1 }}>
          <Button kind="secondary" onClick={onHide}>
            Cancel
          </Button>
          {willSupplyInspection === false && this.ineligibleRepairsInAppraisal() ? (
            <Button kind="primary" onClick={this.saveAndSendAAN.bind(this)} renderIcon={Save16}>
              <i className="fa fa-bomb" /> Save changes &amp; send AAN
            </Button>
          ) : (
            <Button kind="primary" onClick={this.onSave.bind(this)} renderIcon={Save16}>
              Save changes
            </Button>
          )}
        </div>
      );
    } else {
      buttons = (
        <div style={{ flex: 1 }}>
          <Button onClick={onHide}>OK</Button>
        </div>
      );
    }

    return (
      <HurdleView app={app} hurdle={hurdle} onHide={onHide}>
        <div className="property-condition__modal-body--scroll">
          <RepairSummary
            prefix="appraisal"
            repairs={repairs}
            salesPrice={this.getSalesPrice()}
            costToCure={this.getCostToCure()}
            costToCurePercent={this.getCostToCureAsPercent()}
          />
          <RepairTable
            prefix="appraisal"
            repairs={repairs}
            confirmedComplete={confirmedComplete}
            showSellerFix={remedyMethod === "seller_fix"}
            onToggleFixCompleted={this.onChangeComplete.bind(this)}
          />
          <div style={{ display: "flex" }}>
            <div>
              <div>Will borrower supply an inspection report?</div>
              <div className="property-condition__dropdown">
                <Select
                  placeholder="—"
                  searchable={false}
                  value={willSupplyInspection}
                  options={[
                    { value: false, label: "No" },
                    { value: true, label: "Yes" },
                  ]}
                  onChange={this.onChangeAppField.bind(this, "willSupplyInspection")}
                />
              </div>
            </div>
            {showRemedy && (
              <div className="property-condition__dropdown-spacer">
                <i className="fa fa-long-arrow-right" />
              </div>
            )}
            {showRemedy && (
              <div>
                <div>What remedy does borrower want?</div>
                <div className="property-condition__dropdown">
                  <Select
                    placeholder="—"
                    searchable={false}
                    value={remedyMethod}
                    options={this.getRemedyOptions()}
                    onChange={this.onChangeAppField.bind(this, "remedyMethod")}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        <footer className={styles.footer}>
          <Button danger onClick={this.stepBack.bind(this)}>
            <i className="fa fa-undo" /> Step back to appraisal repairs
          </Button>
          {buttons}
        </footer>
      </HurdleView>
    );
  }
}

export default connect<any, any, any>(null, mapDispatchToProps)(AppraisalSummary);
